import * as React from 'react';

import Icon, { IconNames } from '@/components/common/Icon';
import { cn } from '@/utils';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  disabled?: boolean;
  name?: string;
  title?: string;
  titleClassName?: string;
  icon?: {
    name: IconNames; // required
    className?: string;
    color?: string;
    size?: number;
  };
  startSlotRender?: React.ReactNode;
  endSlotRender?: React.ReactNode;
  footerRender?: React.ReactNode;
  error?: string;
  clearButton?: boolean;
  variant?: 'ghost' | 'default';
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      icon,
      name,
      title,
      titleClassName,
      disabled,
      endSlotRender,
      footerRender,
      error,
      clearButton = true,
      variant = 'default',
      startSlotRender,
      ...props
    },
    ref
  ) => {
    const iconClassName = icon?.className;
    const [value, setValue] = React.useState(props.value ?? '');
    const inputRef = React.useRef<HTMLInputElement>(null);
    // Allow external ref
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    React.useEffect(() => {
      if (props.value !== undefined) setValue(props.value as string);
      else {
        setValue('');
        if (props.onChange) {
          const event = {
            ...new Event('change', { bubbles: true }),
            target: { value: '' },
          } as unknown as React.ChangeEvent<HTMLInputElement>;
          props.onChange(event);
        }
      }
    }, [props.value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      props.onChange?.(e);
    };

    const handleClear = () => {
      setValue('');
      if (inputRef.current) {
        inputRef.current.value = '';
        // Fire onChange event for controlled components
        const event = new Event('input', { bubbles: true });
        inputRef.current.dispatchEvent(event);
        inputRef.current.focus();
      }
      if (props.onChange) {
        const event = {
          ...new Event('change', { bubbles: true }),
          target: { value: '' },
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        props.onChange(event);
      }
    };

    return (
      <div>
        {title && (
          <label
            className={cn(
              'text-gray-700 text-sm font-normal px-3 mb-1',
              titleClassName
            )}
            htmlFor={name}
          >
            {title}
          </label>
        )}

        <div className="relative flex items-center">
          {icon && icon.name ? (
            <Icon
              name={icon.name}
              className={cn(
                'absolute top-1/2 -translate-y-1/2 left-4 pointer-events-none',
                iconClassName
              )}
              color={icon.color}
              size={icon.size}
            />
          ) : (
            startSlotRender && (
              <div className="gap-2 flex items-center absolute top-1/2 -translate-y-1/2 start-4">
                {startSlotRender}
              </div>
            )
          )}
          <input
            type={type}
            className={cn(
              'outline-none',
              variant === 'default' &&
                'ring-0 flex h-full w-full rounded-xl border border-additional-divider bg-transparent py-2.5 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              icon ? 'ps-10 pe-10' : 'px-3',
              disabled &&
                (variant === 'default'
                  ? 'bg-gray-50 border border-gray-200'
                  : 'bg-transparent text-gray-400'),
              error && 'border-torch-red-500',
              className
            )}
            ref={inputRef}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            {...props}
          />
          <div className="gap-2 flex items-center absolute top-1/2 -translate-y-1/2 end-4">
            {(!!value || value === 0) && !disabled && clearButton && (
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                onClick={handleClear}
                tabIndex={-1}
              >
                <Icon
                  name="CloseCircle"
                  size={16}
                  variant="Bold"
                  className="text-gray-500"
                />
              </button>
            )}
            {endSlotRender}
          </div>
        </div>
        {(error || footerRender) && (
          <div className="flex items-center justify-between pt-1 px-3">
            {error && (
              <span
                className={cn(
                  'text-torch-red-500 text-xs font-medium cursor-pointer',
                  !error ? 'opacity-0' : ''
                )}
              >
                {error}
              </span>
            )}
            {footerRender}
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
